import { QuestionField, QuestionValidationProvider, useQuestionValidationFeedback } from 'components/questions/QuestionField';
import { ShortAnswerFields } from 'components/questions/ShortAnswerFields';
import { validateQuestion } from 'components/questions/questionValidation';
import { SubjectSelect } from 'components/questionBank/SubjectSelect';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Check, GripVertical, Plus, Trash2, History, CircleCheck, Crown, Eye } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'components/ui/sheet';
import { RichTextEditor } from 'modules/editor';
import { QuestionTypeEditor, DISPATCHED_QUESTION_TYPES, type QuestionTypeData } from 'components/questions/QuestionTypeEditor';
import { QuestionTopicInput } from 'components/questions/QuestionTopicInput';
import { useQuestionTopicField } from 'components/questions/questionTopic';
import type { Subject } from 'modules/subject/types';
import type { CreateBankQuestionData } from 'modules/questionBank/api';
import type { BankQuestion } from 'modules/questionBank/types';
import type { QuestionType } from '@examing/types';
import { nanoid } from 'nanoid';
import { cn } from 'lib/utils';
import { useIsMobile } from 'hooks/use-media-query';
import { getQuestionOptionLabel, normalizeQuestionAnswers } from 'lib/questionOptions';
import { QUESTION_TYPE_META, isAdvancedQuestionType } from './questionTypeMeta';
import { FEATURES, useFeature, useOpenUpgradeForFeature } from 'modules/entitlements';
import { LivePlayerPreview, type PreviewDraft } from './LivePlayerPreview';
import { AiImageDescriptionField } from 'components/questions/AiImageDescriptionField';

type Option = { id: string; value: string };
const CHOICE_TYPES: QuestionType[] = ['MULTIPLE_CHOICE', 'MULTIPLE_RESPONSE', 'TRUE_FALSE'];
const TYPE_DATA_KEYS: (keyof QuestionTypeData)[] = [
  'answerMode', 'acceptedAnswers', 'maxWords', 'matchingOptions', 'blanks', 'reorderItems', 'correctOrder',
  'mediaUrl', 'aiImageDescription', 'rubric', 'passageText', 'passageTitle', 'subQuestions', 'pages',
];

const TYPE_OPTIONS: QuestionType[] = [
  'MULTIPLE_CHOICE', 'MULTIPLE_RESPONSE', 'TRUE_FALSE', 'SHORT_ANSWER', 'MATCHING',
  'FILL_IN_BLANKS', 'OPEN_ENDED', 'REORDER', 'PASSAGE',
];

const defaultOptions = (type: QuestionType): Option[] => {
  if (type === 'TRUE_FALSE') return [{ id: 'opt_true', value: 'True' }, { id: 'opt_false', value: 'False' }];
  if (type === 'MATCHING') return [];
  return [{ id: 'opt_0', value: '' }, { id: 'opt_1', value: '' }, { id: 'opt_2', value: '' }, { id: 'opt_3', value: '' }];
};
const defaultAnswer = (type: QuestionType): string | string[] =>
  type === 'TRUE_FALSE' ? 'opt_true' : type === 'MULTIPLE_RESPONSE' ? [] : '';

const extractTypeData = (src?: Partial<QuestionTypeData>): QuestionTypeData => {
  const out: QuestionTypeData = src ? {} : { answerMode: 'exact' };
  if (src) for (const k of TYPE_DATA_KEYS) if (src[k] !== undefined) (out as Record<string, unknown>)[k] = src[k];
  return out;
};

export interface QuestionEditorTwoPaneProps {
  subjects: Subject[];
  initialData?: BankQuestion;
  defaultSubjectId?: string;
  onSubmit: (data: CreateBankQuestionData, opts?: { stay?: boolean }) => Promise<void>;
  onDone: () => void;
}

/** Debounced autosave replaces an explicit Save. Version history is session-scoped — no revisions backend. */
export const QuestionEditorTwoPane: React.FC<QuestionEditorTwoPaneProps> = ({
  subjects,
  initialData,
  defaultSubjectId,
  onSubmit,
  onDone,
}) => {
  const { t } = useTranslation();
  // On phones this editor renders in-flow inside the MobileShell, whose floating
  // capsule nav hovers over the bottom of the (single, preview hidden) form pane.
  // Pad the pane past it so the last fields (subject/difficulty) clear the dock —
  // same clearance the dedicated mobile screens use (ScreenBody, primitives.tsx).
  const isMobile = useIsMobile();
  // Advanced (non-basic) question types are a Growth+ capability.
  const canAdvancedTypes = useFeature(FEATURES.ADVANCED_QUESTION_TYPES);
  const openUpgrade = useOpenUpgradeForFeature();
  const isLockedType = (value: string) => !canAdvancedTypes && isAdvancedQuestionType(value);
  // Normalize the persisted options/answer once so the fields populate and the
  // "mark correct" control lights up regardless of the stored shape (value vs
  // text, or a value-based correctAnswer instead of an option id).
  const seeded = useMemo(
    () => normalizeQuestionAnswers(initialData?.options, initialData?.correctAnswer),
    // Seed once from the loaded question; the editor owns the state afterwards.
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const [subjectId, setSubjectId] = useState(initialData?.subjectId ?? defaultSubjectId ?? '');
  const [type, setType] = useState<QuestionType>(initialData?.type ?? 'MULTIPLE_CHOICE');
  const [points, setPoints] = useState(initialData?.points ?? 2);
  const [prompt, setPrompt] = useState(initialData?.prompt ?? '');
  const [options, setOptions] = useState<Option[]>(
    initialData ? seeded.options : defaultOptions('MULTIPLE_CHOICE'),
  );
  const [correctAnswer, setCorrectAnswer] = useState<string | string[] | Record<string, string>>(
    initialData ? seeded.correctAnswer : '',
  );
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>(
    (initialData?.difficulty as 'easy' | 'medium' | 'hard') ?? 'easy',
  );
  const [typeData, setTypeData] = useState<QuestionTypeData>(() => extractTypeData(initialData));
  const [matchPairs, setMatchPairs] = useState<Array<{ key: string; value: string }>>(
    initialData?.matchPairs ?? [{ key: '', value: '' }, { key: '', value: '' }],
  );
  const topic = useQuestionTopicField(initialData?.topic);
  const topicReset = topic.reset;
  const [previewOpen, setPreviewOpen] = useState(false);

  const [version, setVersion] = useState(initialData ? 1 : 0);
  const [saving, setSaving] = useState(false);
  const dragIndex = useRef<number | null>(null);
  const seededRef = useRef(true);
  const activeSaveRef = useRef<Promise<boolean> | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [finishing, setFinishing] = useState(false);

  const isDispatched = DISPATCHED_QUESTION_TYPES.includes(type);
  const promptOwnedByEditor = (['FILL_IN_BLANKS', 'PASSAGE'] as QuestionType[]).includes(type);

  // Rehydrate state if initialData arrives or updates after initial mount
  const prevInitialIdRef = useRef<string | undefined>(initialData?.id);
  useEffect(() => {
    if (initialData && initialData.id !== prevInitialIdRef.current) {
      prevInitialIdRef.current = initialData.id;
      const normalized = normalizeQuestionAnswers(initialData.options, initialData.correctAnswer);
      setSubjectId(initialData.subjectId ?? defaultSubjectId ?? '');
      setType(initialData.type ?? 'MULTIPLE_CHOICE');
      setPoints(initialData.points ?? 2);
      setPrompt(initialData.prompt ?? '');
      setOptions(normalized.options.length > 0 ? normalized.options : defaultOptions(initialData.type ?? 'MULTIPLE_CHOICE'));
      setCorrectAnswer(normalized.correctAnswer);
      setDifficulty((initialData.difficulty as 'easy' | 'medium' | 'hard') ?? 'easy');
      setTypeData(extractTypeData(initialData));
      setMatchPairs(initialData.matchPairs ?? [{ key: '', value: '' }, { key: '', value: '' }]);
      topicReset(initialData.topic);
    }
  }, [initialData, defaultSubjectId, topicReset]);

  const validation = useMemo(() => validateQuestion({ subjectId, type, prompt, options, correctAnswer, matchPairs, points, ...typeData }),
  [subjectId, type, prompt, options, correctAnswer, matchPairs, points, typeData]);
  const { formRef, revealErrors, hasAttempted, visibleIssues } = useQuestionValidationFeedback(validation.issues);
  const isValid = validation.isValid;
  const payload: CreateBankQuestionData = {
    subjectId,
    type,
    prompt: (type === 'PASSAGE' ? typeData.passageText ?? prompt : prompt).trim(),
    options: CHOICE_TYPES.includes(type) ? options : undefined,
    matchPairs: type === 'MATCHING' ? matchPairs : undefined,
    correctAnswer: isDispatched ? '' : correctAnswer,
    points,
    difficulty,
    ...topic.patch,
    ...typeData,
  };
  const payloadKey = JSON.stringify(payload);
  const savedPayloadRef = useRef<string | null>(initialData ? payloadKey : null);
  const latestRef = useRef({ payload, payloadKey, validation, onSubmit, failureMessage: t('failed_to_save_draft_changes') });
  latestRef.current = { payload, payloadKey, validation, onSubmit, failureMessage: t('failed_to_save_draft_changes') };

  // All callers await the same in-flight write, then save the latest draft.
  // This keeps create/update serial without dropping queued edits or promises.
  const saveLatest = useCallback(async (): Promise<boolean> => {
    while (activeSaveRef.current) {
      if (!await activeSaveRef.current) return false;
    }
    const current = latestRef.current;
    if (!current.validation.isValid) {
      revealErrors();
      return false;
    }
    if (savedPayloadRef.current === current.payloadKey) return true;
    setSaveError(null);
    setSaving(true);
    const operation = Promise.resolve().then(async () => {
      try {
        await current.onSubmit(current.payload, { stay: true });
        savedPayloadRef.current = current.payloadKey;
        setVersion((value) => value + 1);
        return true;
      } catch (error) {
        const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
        const detail = typeof message === 'string' ? message : Array.isArray(message)
          ? message.filter((item): item is string => typeof item === 'string').join('; ') : '';
        setSaveError(detail || current.failureMessage);
        return false;
      } finally {
        activeSaveRef.current = null;
        setSaving(false);
      }
    });
    activeSaveRef.current = operation;
    return operation;
  }, [revealErrors]);

  useEffect(() => {
    if (seededRef.current) {
      seededRef.current = false;
      return;
    }
    setSaveError(null);
    if (!isValid) return;
    debounceRef.current = setTimeout(() => { void saveLatest(); }, 900);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [payloadKey, isValid, saveLatest]);

  const handleDone = async () => {
    revealErrors();
    if (!latestRef.current.validation.isValid || finishing) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setFinishing(true);
    try {
      // An edit during an in-flight save must also finish before navigation.
      do {
        if (!await saveLatest()) return;
      } while (savedPayloadRef.current !== latestRef.current.payloadKey);
      onDone();
    } finally {
      setFinishing(false);
    }
  };
  const visibleError = hasAttempted && !isValid ? t('question_validation_fix_fields') : saveError;
  const isSaved = savedPayloadRef.current === payloadKey;

  const meta = QUESTION_TYPE_META[type];
  const typeLabel = meta?.labelKey ? t(meta.labelKey) : type;

  const draft: PreviewDraft = {
    type,
    points,
    prompt,
    options,
    correctAnswer,
    typeLabel,
    questionNumber: 1,
    // Type-specific authoring state, so the preview can render blanks,
    // ordering, categories, hot-text spans and passages as they're edited —
    // not just the stem.
    ...typeData,
    matchPairs,
  };

  // Option handlers
  const addOption = () => setOptions((p) => [...p, { id: `opt_${nanoid(4)}`, value: '' }]);
  const updateOption = (id: string, value: string) => setOptions((p) => p.map((o) => (o.id === id ? { ...o, value } : o)));
  const removeOption = (id: string) => {
    setOptions((p) => p.filter((o) => o.id !== id));
    if (correctAnswer === id) setCorrectAnswer('');
    if (Array.isArray(correctAnswer)) setCorrectAnswer((correctAnswer as string[]).filter((a) => a !== id));
  };
  const reorder = (to: number) => {
    const from = dragIndex.current;
    if (from == null || from === to) return;
    setOptions((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    dragIndex.current = null;
  };
  const markCorrect = (id: string) => {
    if (type === 'MULTIPLE_RESPONSE') {
      const arr = Array.isArray(correctAnswer) ? (correctAnswer as string[]) : [];
      setCorrectAnswer(arr.includes(id) ? arr.filter((a) => a !== id) : [...arr, id]);
    } else {
      setCorrectAnswer(id);
    }
  };
  const isCorrect = (id: string) =>
    Array.isArray(correctAnswer) ? correctAnswer.includes(id) : correctAnswer === id;

  return (
    <QuestionValidationProvider issues={visibleIssues}>
    <div ref={formRef} className="flex h-[calc(100vh-var(--header-h,3rem))] flex-col">
      {/* Header */}
      <header className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-border bg-card px-4">
        <div className="flex min-w-0 flex-1 items-center gap-2 text-sm">
          {/* Breadcrumb root — hidden below lg to save width; the current-page
              label (and Back via the Done button / breadcrumb on lg+) remains. */}
          <Link
            to="/app/questions"
            className="hidden shrink-0 font-medium text-muted-foreground transition-colors hover:text-foreground lg:inline"
          >
            {t('question_bank')}
          </Link>
          <span className="hidden shrink-0 text-muted-foreground/50 lg:inline">/</span>
          <span className="truncate font-semibold text-foreground">{initialData ? t('edit_question') : t('add_question_to_bank')}</span>
          {(saving || (version > 0 && isSaved)) && (
            <span
              className="ml-1 inline-flex shrink-0 items-center gap-1 rounded-md bg-success-50 px-2 py-0.5 text-xs font-medium text-success-700 dark:bg-success-900/30 dark:text-success-300"
              title={saving ? t('editor_saving') : t('editor_autosaved_version', { version })}
            >
              <CircleCheck className="h-3.5 w-3.5" />
              {/* Text collapses to just the check icon below xl to keep the row on one line. */}
              <span className="hidden xl:inline">{saving ? t('editor_saving') : t('editor_autosaved_version', { version })}</span>
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                <History className="h-3.5 w-3.5 lg:mr-1.5" />
                <span className="hidden lg:inline">{t('editor_version_history')}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64">
              <p className="mb-2 text-sm font-semibold">{t('editor_version_history')}</p>
              {version > 0 ? (
                <ul className="space-y-1 text-sm">
                  {Array.from({ length: Math.min(version, 5) }).map((_, i) => {
                    const v = version - i;
                    return (
                      <li key={v} className="flex items-center justify-between rounded-md px-2 py-1 hover:bg-muted">
                        <span>{t('editor_autosaved_version', { version: v })}</span>
                        {i === 0 && <span className="text-xs font-medium text-muted-foreground">{t('current')}</span>}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-xs text-muted-foreground">{t('editor_no_history')}</p>
              )}
              <p className="mt-2 border-t border-border pt-2 text-[11px] text-muted-foreground">{t('editor_history_session_note')}</p>
            </PopoverContent>
          </Popover>
          <Button
            variant={previewOpen ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setPreviewOpen((open) => !open)}
            aria-label={t('preview')}
            aria-pressed={previewOpen}
            title={t('preview')}
          >
            <Eye className="h-3.5 w-3.5 sm:mr-1.5" aria-hidden />
            <span className="hidden sm:inline">{t('preview')}</span>
          </Button>
          <Button size="sm" onClick={handleDone} disabled={finishing}>{finishing ? t('editor_saving') : t('collections.done')}</Button>
        </div>
      </header>

      {visibleError && <div role="alert" className="border-b border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">{visibleError}</div>}

      {/* Editor body */}
      <div className="min-h-0 flex-1">
        {/* Form */}
        <div
          className="h-full min-h-0 overflow-y-auto p-4 sm:p-6"
          style={isMobile ? { paddingBottom: 'calc(92px + env(safe-area-inset-bottom))' } : undefined}
        >
          <div className="mx-auto max-w-3xl space-y-5">
            {/* Type + Points */}
            <div className="grid grid-cols-[2fr_1fr] gap-3">
              <div className="space-y-1.5">
                <label className="text-[13px] font-medium text-muted-foreground">{t('question_type')}</label>
                <Select
                  value={type}
                  onValueChange={(v) => {
                    // Selecting a locked advanced type opens the upgrade dialog
                    // and keeps the current basic type instead of switching.
                    if (isLockedType(v)) {
                      openUpgrade(FEATURES.ADVANCED_QUESTION_TYPES);
                      return;
                    }
                    if (v === type) return;
                    const nextType = v as QuestionType;
                    setType(nextType);
                    // The picture description belongs to the question, not the type.
                    setTypeData((previous) => ({ answerMode: 'exact', ...(previous.aiImageDescription ? { aiImageDescription: previous.aiImageDescription } : {}) }));
                    // Reset only for an author's type change, never when
                    // loading saved data or replaying effects in StrictMode.
                    setOptions(defaultOptions(nextType));
                    setCorrectAnswer(defaultAnswer(nextType));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPE_OPTIONS.map((ty) => (
                      <SelectItem key={ty} value={ty}>
                        <span className="flex items-center gap-1.5">
                          {QUESTION_TYPE_META[ty]?.labelKey ? t(QUESTION_TYPE_META[ty].labelKey) : ty}
                          {isLockedType(ty) && <Crown className="size-3 text-amber-500" aria-hidden />}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[13px] font-medium text-muted-foreground">{t('points')}</label>
                <QuestionField name="points"><Input
                  type="number"
                  min={0}
                  value={points}
                  onChange={(e) => setPoints(Math.max(0, Number(e.target.value) || 0))}
                /></QuestionField>
              </div>
            </div>

            {/* Question */}
            {!promptOwnedByEditor && (
              <div className="space-y-1.5">
                <label className="text-[13px] font-medium text-muted-foreground">{t('question')}</label>
                <QuestionField name="prompt"><RichTextEditor
                  value={prompt}
                  onChange={setPrompt}
                  preset="question"
                  placeholder={t('enter_the_question')}
                  uploadContext={subjectId ? `question-bank/${subjectId}` : 'question-bank'}
                  minHeight={120}
                /></QuestionField>
              </div>
            )}

            {/* Extended type editors */}
            {isDispatched && (
              <QuestionTypeEditor
                type={type}
                prompt={prompt}
                onPromptChange={setPrompt}
                data={typeData}
                onChange={(patch) => setTypeData((prev) => ({ ...prev, ...patch }))}
              />
            )}

            {/* Answer options (MC / MR / TF) */}
            {CHOICE_TYPES.includes(type) && (
              <QuestionField name="correctAnswer"><div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                  <label className="text-[13px] font-medium text-muted-foreground">{t('answer_options')}</label>
                  <span className="text-xs text-muted-foreground">
                    {type === 'MULTIPLE_RESPONSE' ? t('options_select_all') : t('editor_mark_correct')}
                  </span>
                </div>
                <QuestionField name="options"><div className="space-y-2">
                  {options.map((opt, i) => {
                    const correct = isCorrect(opt.id);
                    const canDrag = type !== 'TRUE_FALSE';
                    return (
                      <div
                        key={opt.id}
                        draggable={canDrag}
                        onDragStart={() => (dragIndex.current = i)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => reorder(i)}
                        className={cn(
                          'flex items-center gap-1.5 rounded-lg border px-1.5 py-1.5 transition-colors sm:gap-2 sm:px-2.5 sm:py-2',
                          correct ? 'border-success-500 bg-success-50/70 dark:bg-success-900/20' : 'border-border bg-card',
                        )}
                      >
                        {/* The grip is a native HTML5 drag handle, which touch
                            devices don't fire — hide it on phones and give the
                            width back to the option input. */}
                        {canDrag && <GripVertical className="hidden h-4 w-4 shrink-0 cursor-grab text-muted-foreground/50 sm:block" />}
                        {/* The -m-1.5/p-1.5 pair keeps the tap target at 28px
                            while the drawn control shrinks to 16px on phones;
                            padding and negative margin cancel, so the row's
                            layout (and the gap to the input) is unchanged. */}
                        <button
                          type="button"
                          onClick={() => markCorrect(opt.id)}
                          aria-label={t('mark_as_correct')}
                          aria-pressed={correct}
                          className="-m-1.5 shrink-0 p-1.5"
                        >
                          <span
                            className={cn(
                              'flex h-4 w-4 items-center justify-center border transition-colors sm:h-5 sm:w-5',
                              // Square marker = checkbox (matches ui/checkbox), circle = radio.
                              type === 'MULTIPLE_RESPONSE' ? 'rounded-[4px]' : 'rounded-full',
                              correct ? 'border-success-600 bg-success-600 text-white' : 'border-input',
                            )}
                          >
                            {correct &&
                              (type === 'MULTIPLE_RESPONSE' ? (
                                <Check className="h-2.5 w-2.5 sm:h-3 sm:w-3" strokeWidth={3} />
                              ) : (
                                <span className="h-1.5 w-1.5 rounded-full bg-white sm:h-2 sm:w-2" />
                              ))}
                          </span>
                        </button>
                        <div className="min-w-0 flex-1">
                          {type === 'TRUE_FALSE' ? (
                            <span className="text-sm text-foreground">{getQuestionOptionLabel(type, opt.value, t)}</span>
                          ) : (
                            <QuestionField name={`options.${i}.value`}><RichTextEditor
                              value={opt.value}
                              onChange={(v) => updateOption(opt.id, v)}
                              preset="answer"
                              placeholder={t('option')}
                              uploadContext={subjectId ? `question-bank/${subjectId}` : 'question-bank'}
                              minHeight={36}
                            /></QuestionField>
                          )}
                        </div>
                        {/* The filled control + success border already read as
                            "correct"; the word is desktop-only so the input
                            keeps the width on phones. */}
                        {correct && (
                          <span className="hidden shrink-0 text-xs font-semibold text-success-700 dark:text-success-400 sm:inline">
                            {t('editor_correct')}
                          </span>
                        )}
                        {canDrag && options.length > 2 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 shrink-0 text-muted-foreground sm:h-7 sm:w-7"
                            onClick={() => removeOption(opt.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div></QuestionField>
                {type !== 'TRUE_FALSE' && options.length < 6 && (
                  <Button variant="ghost" size="sm" onClick={addOption}>
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    {t('add_option')}
                  </Button>
                )}
              </div></QuestionField>
            )}

            {/* Short answer */}
            {type === 'SHORT_ANSWER' && <ShortAnswerFields data={typeData} correctAnswer={correctAnswer} onChange={(patch, answer) => {
              setTypeData((previous) => ({ ...previous, ...patch }));
              if (answer !== undefined) setCorrectAnswer(answer);
            }} />}

            {/* Matching */}
            {type === 'MATCHING' && (
              <QuestionField name="matchPairs"><div className="space-y-2">
                <label className="text-[13px] font-medium text-muted-foreground">{t('match_pairs')}</label>
                {matchPairs.map((pair, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <QuestionField name={`matchPairs.${i}.key`} className="flex-1"><Input placeholder={t('term')} value={pair.key} onChange={(e) => setMatchPairs((p) => p.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))} /></QuestionField>
                    <span className="text-muted-foreground">→</span>
                    <QuestionField name={`matchPairs.${i}.value`} className="flex-1"><Input placeholder={t('definition')} value={pair.value} onChange={(e) => setMatchPairs((p) => p.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} /></QuestionField>
                    {matchPairs.length > 2 && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setMatchPairs((p) => p.filter((_, j) => j !== i))}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="ghost" size="sm" onClick={() => setMatchPairs((p) => [...p, { key: '', value: '' }])}>
                  <Plus className="mr-1 h-3.5 w-3.5" />
                  {t('add_pair')}
                </Button>
              </div></QuestionField>
            )}

            {/* Topic */}
            <QuestionTopicInput
              value={topic.value}
              onChange={topic.onChange}
              labelClassName="text-[13px] text-muted-foreground"
            />

            {/* AI-only picture description */}
            <AiImageDescriptionField
              value={typeData.aiImageDescription ?? ''}
              onChange={(next) => setTypeData((prev) => ({ ...prev, aiImageDescription: next }))}
            />

            {/* Subject + Difficulty */}
            <div className="grid grid-cols-2 gap-3 border-t border-border pt-4">
              <div className="space-y-1.5">
                <label className="text-[13px] font-medium text-muted-foreground">{t('subject')}</label>
                <QuestionField name="subjectId">
                  <SubjectSelect
                    value={subjectId}
                    onChange={setSubjectId}
                    subjects={subjects}
                  />
                </QuestionField>
              </div>
              <div className="space-y-1.5">
                <label className="text-[13px] font-medium text-muted-foreground">{t('difficulty')}</label>
                <div className="inline-flex h-9 w-full items-center rounded-lg border border-border bg-muted/40 p-0.5">
                  {(['easy', 'medium', 'hard'] as const).map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setDifficulty(d)}
                      className={cn(
                        'flex-1 rounded-md px-2 py-1 text-sm font-medium transition-colors',
                        difficulty === d ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {t(d === 'medium' ? 'difficulty_medium_short' : d)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      <Sheet open={previewOpen} onOpenChange={setPreviewOpen}>
        <SheetContent
          side="right"
          className="flex w-full max-w-none flex-col gap-0 overflow-hidden px-0 pb-0 pt-12 sm:max-w-[680px] lg:max-w-[760px]"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>{t('editor_live_preview')}</SheetTitle>
            <SheetDescription>{t('editor_preview_hint')}</SheetDescription>
          </SheetHeader>
          <LivePlayerPreview draft={draft} />
        </SheetContent>
      </Sheet>
    </div>
    </QuestionValidationProvider>
  );
};

export default QuestionEditorTwoPane;
